import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAssistant } from '../../contexts/AssistantContext';
import { tourService } from '../../services/tourService';
import { Tour } from '../../types';
import {
    ChatBubbleLeftRightIcon,
    XMarkIcon,
    ChevronRightIcon,
    SparklesIcon,
    ArrowPathIcon,
    CalendarIcon,
    UserGroupIcon,
    CurrencyDollarIcon,
    TagIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

const SafariAssistant: React.FC = () => {
    const { actualTheme } = useTheme();
    const {
        isVisible,
        mode,
        lastViewed,
        setMode,
        minimize,
        maximize,
        suggestions,
        setSuggestions,
        userPreferences,
        setUserPreferences
    } = useAssistant();

    const navigate = useNavigate();
    const [categories, setCategories] = useState<string[]>([]);
    const [adults, setAdults] = useState(2);
    const [children, setChildren] = useState(0);

    // Load categories on mount
    useEffect(() => {
        const loadCategories = async () => {
            const tours = await tourService.getAllTours();
            const uniqueCategories = Array.from(new Set(tours.map(t => t.category)));
            setCategories(uniqueCategories);
        };
        loadCategories();
    }, []);

    // Load filtered suggestions when in suggestions mode
    useEffect(() => {
        if (mode === 'suggestions') {
            const loadFilteredTours = async () => {
                let tours = await tourService.getAllTours();

                // Filter by category if selected
                if (userPreferences.category && userPreferences.category !== 'All') {
                    tours = tours.filter(t => t.category === userPreferences.category);
                }

                // Sort by rating
                tours.sort((a, b) => b.rating - a.rating);

                setSuggestions(tours.slice(0, 6));
            };
            loadFilteredTours();
        }
    }, [mode, userPreferences, setSuggestions]);

    const handleViewTour = (id: string) => {
        navigate(`/tours/${id}`);
        minimize();
    };

    const handleCategorySelect = (category: string) => {
        setUserPreferences({
            ...userPreferences,
            category,
            savedAt: Date.now()
        });
        setMode('group-size');
    };

    const handleGroupSizeSubmit = () => {
        setUserPreferences({
            ...userPreferences,
            groupSize: { adults, children },
            savedAt: Date.now()
        });
        setMode('budget-select');
    };

    const handleBudgetSelect = (budget: string) => {
        setUserPreferences({
            ...userPreferences,
            budget,
            savedAt: Date.now()
        });
        setMode('suggestions');
    };

    // Auto-dismiss timeout: if left in initial prompting mode, minimize after 15s
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (isVisible && (mode === 'initial' || mode === 'welcome-back')) {
            timeoutId = setTimeout(() => {
                minimize();
            }, 15000);
        }
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isVisible, mode, minimize]);

    // --- ANIMATION VARIANTS ---
    const variants: any = {
        hidden: { opacity: 0, y: 40, scale: 0.95, filter: 'blur(10px)' },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
            transition: {
                type: 'spring',
                damping: 25,
                stiffness: 300,
                mass: 0.8
            }
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            y: 20,
            filter: 'blur(10px)',
            transition: { duration: 0.2, ease: "easeOut" }
        }
    };

    // --- THEME STYLES ---
    const isDark = actualTheme === 'dark';

    // Base colors
    const bgClass = isDark
        ? "bg-gradient-to-br from-slate-800/95 via-slate-800/90 to-slate-900/95 border-slate-700/50"
        : "bg-gradient-to-br from-white/95 via-gray-50/90 to-white/95 border-white/50";

    const textClass = isDark ? "text-white" : "text-gray-900";
    const textMutedClass = isDark ? "text-white/70" : "text-gray-600";
    const borderClass = isDark ? "border-white/10" : "border-gray-200";
    const headerBgClass = isDark ? "bg-black/20" : "bg-gray-100/50";
    const buttonBgClass = isDark ? "bg-white/5 hover:bg-white/10" : "bg-gray-100 hover:bg-gray-200";
    const shadowClass = isDark
        ? "shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.05)]"
        : "shadow-[0_8px_32px_rgba(0,0,0,0.1),0_0_0_1px_rgba(0,0,0,0.05)]";

    // Minimized bubble is now handled by FloatingHub
    if (mode === 'minimized') {
        return null;
    }

    if (!isVisible || mode === 'hidden') return null;

    return (
        <AnimatePresence mode="wait">
            <motion.div
                layoutId="safari-assistant"
                key="assistant-window"
                variants={variants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fixed bottom-28 sm:bottom-8 right-4 sm:right-8 z-[100] w-[calc(100%-2rem)] sm:w-full sm:max-w-sm"
            >
                <div className={`${bgClass} backdrop-blur-xl border ${borderClass} ${shadowClass} rounded-3xl overflow-hidden ${textClass} relative`}>
                    {/* Decorative Gloss */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                    {/* Header */}
                    <div className={`px-6 py-4 border-b ${borderClass} flex justify-between items-center ${headerBgClass}`}>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary-500/80 flex items-center justify-center">
                                <ChatBubbleLeftRightIcon className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-bold text-sm tracking-wide">Safari Assistant</span>
                        </div>
                        <button onClick={minimize} className={`p-1 ${buttonBgClass} rounded-full transition-colors`}>
                            <XMarkIcon className={`w-5 h-5 ${textMutedClass}`} />
                        </button>
                    </div>

                    {/* Content Body */}
                    <div className="p-6 relative max-h-[500px] overflow-y-auto">

                        {/* MODE: INITIAL */}
                        {mode === 'initial' && (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="space-y-4"
                            >
                                <h3 className="text-xl font-bold">Hi there!</h3>
                                <p className={`${textMutedClass} text-sm leading-relaxed`}>
                                    I can help you find the perfect safari package. Let me ask you a few quick questions!
                                </p>
                                <div className="grid grid-cols-2 gap-3 pt-2">
                                    <button
                                        onClick={() => setMode('category-select')}
                                        className="py-3 px-4 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-sm font-bold shadow-lg transition-all hover:scale-105"
                                    >
                                        Let's start
                                    </button>
                                    <button
                                        onClick={minimize}
                                        className={`py-3 px-4 rounded-xl ${buttonBgClass} text-sm font-bold transition-all`}
                                    >
                                        Just browsing
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {/* MODE: WELCOME BACK */}
                        {mode === 'welcome-back' && lastViewed && (
                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                className="space-y-4"
                            >
                                <h3 className="text-lg font-bold">Welcome back!</h3>
                                <p className={`${textMutedClass} text-sm`}>
                                    You were looking at <b>{lastViewed.title}</b>.
                                </p>

                                <div className={`relative h-32 rounded-xl overflow-hidden group cursor-pointer border ${borderClass}`} onClick={() => handleViewTour(lastViewed.tourId)}>
                                    <img src={lastViewed.image} alt={lastViewed.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                                        <span className="text-sm text-white font-bold flex items-center gap-1">
                                            Continue <ChevronRightIcon className="w-4 h-4" />
                                        </span>
                                    </div>
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setMode('category-select')}
                                        className={`flex-1 py-2 text-xs font-medium ${textMutedClass} hover:${isDark ? 'text-white' : 'text-primary-600'} flex items-center justify-center gap-1 transition-colors`}
                                    >
                                        <ArrowPathIcon className="w-3 h-3" /> Find something new
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {/* MODE: CATEGORY SELECT */}
                        {mode === 'category-select' && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-4"
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-md font-bold flex items-center gap-2">
                                        <TagIcon className="w-5 h-5" />
                                        What interests you?
                                    </h3>
                                </div>
                                <p className={`${textMutedClass} text-xs mb-4`}>Select a safari category</p>

                                <div className="grid grid-cols-2 gap-3">
                                    {['All', ...categories].map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => handleCategorySelect(cat)}
                                            className={`py-3 px-4 rounded-xl ${buttonBgClass} border ${borderClass} text-sm font-bold transition-all hover:scale-105`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* MODE: GROUP SIZE */}
                        {mode === 'group-size' && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-4"
                            >
                                <h3 className="text-md font-bold flex items-center gap-2">
                                    <UserGroupIcon className="w-5 h-5" />
                                    How many travelers?
                                </h3>

                                <div className="space-y-4">
                                    <div>
                                        <label className={`text-xs ${textMutedClass} mb-2 block`}>Adults</label>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setAdults(Math.max(1, adults - 1))}
                                                className={`w-10 h-10 rounded-lg ${buttonBgClass} font-bold`}
                                            >
                                                -
                                            </button>
                                            <span className="flex-1 text-center font-bold text-lg">{adults}</span>
                                            <button
                                                onClick={() => setAdults(adults + 1)}
                                                className={`w-10 h-10 rounded-lg ${buttonBgClass} font-bold`}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className={`text-xs ${textMutedClass} mb-2 block`}>Children</label>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setChildren(Math.max(0, children - 1))}
                                                className={`w-10 h-10 rounded-lg ${buttonBgClass} font-bold`}
                                            >
                                                -
                                            </button>
                                            <span className="flex-1 text-center font-bold text-lg">{children}</span>
                                            <button
                                                onClick={() => setChildren(children + 1)}
                                                className={`w-10 h-10 rounded-lg ${buttonBgClass} font-bold`}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={handleGroupSizeSubmit}
                                    className="w-full py-3 px-4 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-sm font-bold shadow-lg transition-all hover:scale-105 mt-4"
                                >
                                    Next
                                </button>
                            </motion.div>
                        )}

                        {/* MODE: BUDGET SELECT */}
                        {mode === 'budget-select' && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-4"
                            >
                                <h3 className="text-md font-bold flex items-center gap-2">
                                    <CurrencyDollarIcon className="w-5 h-5" />
                                    What's your budget?
                                </h3>

                                <div className="grid grid-cols-1 gap-3">
                                    {[
                                        { value: 'luxury', label: 'Luxury', desc: 'Premium experience' },
                                        { value: 'standard', label: 'Standard', desc: 'Great value' },
                                        { value: 'budget', label: 'Budget', desc: 'Affordable adventure' }
                                    ].map(option => (
                                        <button
                                            key={option.value}
                                            onClick={() => handleBudgetSelect(option.value)}
                                            className={`py-4 px-4 rounded-xl ${buttonBgClass} border ${borderClass} text-left transition-all hover:scale-105`}
                                        >
                                            <div className="font-bold text-sm">{option.label}</div>
                                            <div className={`text-xs ${textMutedClass}`}>{option.desc}</div>
                                        </button>
                                    ))}
                                </div>
                            </motion.div>
                        )}

                        {/* MODE: SUGGESTIONS (Carousel) */}
                        {mode === 'suggestions' && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-4"
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-md font-bold">Perfect for you!</h3>
                                    <button onClick={() => setMode('category-select')} className={`text-xs ${textMutedClass} hover:${isDark ? 'text-white' : 'text-primary-600'}`}>Refine</button>
                                </div>

                                {userPreferences.category && (
                                    <p className={`text-xs ${textMutedClass}`}>
                                        {userPreferences.category} safaris for {userPreferences.groupSize?.adults || 2} adults
                                        {userPreferences.groupSize?.children ? ` & ${userPreferences.groupSize.children} children` : ''}
                                    </p>
                                )}

                                {/* Horizontal Scroll Container */}
                                <div className="flex gap-4 overflow-x-auto pb-4 snap-x scrollbar-hide -mx-2 px-2">
                                    {suggestions.length === 0 ? (
                                        <div className={`w-full py-8 text-center ${textMutedClass} text-sm`}>Loading ideas...</div>
                                    ) : (
                                        suggestions.map(tour => (
                                            <div
                                                key={tour.id}
                                                className={`min-w-[200px] w-[200px] ${isDark ? 'bg-white/5' : 'bg-gray-100'} rounded-xl border ${borderClass} overflow-hidden snap-center flex-shrink-0 cursor-pointer hover:border-primary-500 transition-all`}
                                                onClick={() => handleViewTour(tour.id)}
                                            >
                                                <div className="h-24 w-full relative">
                                                    <img
                                                        src={tour.images[0]}
                                                        alt={tour.title}
                                                        className="w-full h-full object-cover"
                                                    />
                                                </div>
                                                <div className="p-3">
                                                    <h4 className="text-sm font-bold line-clamp-1 mb-1">{tour.title}</h4>
                                                    <div className={`flex items-center gap-1 text-[10px] ${textMutedClass}`}>
                                                        <CalendarIcon className="w-3 h-3" />
                                                        <span>{tour.duration} Days</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </motion.div>
                        )}

                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default SafariAssistant;
